import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, LinkUnstyled, unit } from '@hcr/ui'
import { isNotUndefined } from '@hcr/utils'

import { useHoliday } from '../../../../contexts'
import { useDestinationDetailsQuery, useRoomDetailsQuery } from '../../../../hooks'
import { LocaleNamespace, Path } from '../../../../models'
import { createLinkGoogleMaps, createRoomNameString, isOwnerUsage, to } from '../../../../utils'
import { WarningMissingData } from '../../warning-missing-data'
import {
  BookingDates,
  LayoutBookingOverview,
  LayoutBookingOverviewContent,
  LayoutBookingOverviewDivider,
  LayoutBookingOverviewHeader,
  LayoutBookingOverviewSection,
} from '../common'

import { BookingOverviewLoading } from './BookingOverviewLoading'

export const BookingOverview: FC = () => {
  const { t } = useTranslation()
  const {
    data: { accommodation, destination },
    ...holiday
  } = useHoliday()

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destination?.destinationId) },
    { enabled: isNotUndefined(destination) }
  )

  const roomDetails = useRoomDetailsQuery(
    { destinationId: String(destination?.destinationId), roomId: String(accommodation?.room_type_id) },
    {
      enabled:
        isNotUndefined(destination) && isNotUndefined(accommodation) && isNotUndefined(accommodation.room_type_id),
    }
  )

  if (holiday.isSuccess && isNotUndefined(accommodation)) {
    const destinationDetailsLink = destinationDetails.isSuccess
      ? createLinkGoogleMaps({
          label: destinationDetails.data.address,
          target: 'external',
          url: destinationDetails.data.address,
        })
      : null

    return (
      <>
        {(destinationDetails.isError || roomDetails.isError) && (
          <Box marginBottom={unit(1.5)}>
            <WarningMissingData />
          </Box>
        )}
        <LayoutBookingOverview>
          <Typography visibility='hidden' component='h1' sx={{ height: unit(0) }} fontWeight='initial'>
            {destinationDetails.data?.name}
          </Typography>

          <LayoutBookingOverviewHeader gap={unit(2)}>
            <Typography variant='bodyM'>{t('booking-overview.your-booking-at')}</Typography>

            <Box
              component='img'
              alt={destinationDetails.data?.name}
              src={destinationDetails.data?.logoUrl}
              width='auto'
              height={unit(9)}
              visibility={destinationDetails.isSuccess ? 'visible' : 'hidden'}
            />
          </LayoutBookingOverviewHeader>
          <LayoutBookingOverviewContent>
            <BookingDates
              checkInDate={accommodation.start_date}
              checkInHour={roomDetails.data?.checkInHour}
              checkOutDate={accommodation.end_date}
              checkOutHour={roomDetails.data?.checkOutHour}
            />
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.accommodation')}>
              <Typography variant='bodyM'>
                {roomDetails.isSuccess ? createRoomNameString(roomDetails.data) : accommodation.room_type}
              </Typography>
              <Icons>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farMoonStars} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.days}
                  </Typography>
                </IconWithLabel>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farFamilyPants} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.adults + accommodation.children}
                  </Typography>
                </IconWithLabel>
              </Icons>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.booking-status')}>
              <Typography variant='bodyM'>
                {t(accommodation.status, { ns: LocaleNamespace.AccommodationStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewSection label={t('booking-overview.payment-status')}>
              <Typography variant='bodyM'>
                {isOwnerUsage(accommodation)
                  ? t('booking-overview.owner-payment')
                  : t(accommodation.payment_status, { ns: LocaleNamespace.PaymentStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            {destinationDetails.isSuccess && destinationDetailsLink && (
              <LayoutBookingOverviewSection label={t('booking-overview.arrival-info')}>
                <Typography
                  component='a'
                  href={String(destinationDetailsLink.to)}
                  key={destinationDetailsLink.label}
                  target='_blank'
                  rel='external'
                  variant='textLinkM'
                  color={COLORS.linknega[500]}
                >
                  {destinationDetailsLink.label}
                  <FontAwesomeIcon style={{ marginLeft: unit(2) }} icon={ICONS.fasLocationArrow} />
                </Typography>
              </LayoutBookingOverviewSection>
            )}
            <Button component={LinkUnstyled} to={to(Path.BookingDetails)} variant='outlined' color='white'>
              {t('booking-overview.see-booking-details')}
            </Button>
          </LayoutBookingOverviewContent>
        </LayoutBookingOverview>
      </>
    )
  }

  return <BookingOverviewLoading />
}

const Icons = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(5.5)};
  margin-top: ${unit(1)};
`

const IconWithLabel = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(1.5)};
`
